"use client";

import { motion, useScroll, useTransform, type Variants } from "framer-motion";
import Image from "next/image";

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1, ease: "easeOut" },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export default function TermsClient() {
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "15%"]);

  return (
    <div className="relative min-h-screen">
      {/* ── Fixed Parallax Aesthetic Background ── */}
      <div className="fixed inset-0 -z-10 bg-[var(--background)]">
        <motion.div
          className="absolute inset-0 h-[120vh] w-full"
          style={{ y: backgroundY }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--background)]/40 via-transparent to-[var(--background)]/90 mix-blend-multiply z-10" />
          <Image
            src="https://images.unsplash.com/photo-1770902634037-08b8e594168b?q=80&w=1974&auto=format&fit=crop"
            alt="Background"
            fill
            className="object-cover opacity-30 md:opacity-40 mix-blend-luminosity grayscale-[30%]"
          />
        </motion.div>
      </div>

      <div className="relative z-10 mx-auto max-w-4xl px-4 py-32 sm:px-8 lg:py-40">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-8 rounded-[2rem] border border-white/10 bg-white/5 p-8 backdrop-blur-xl sm:p-12 shadow-2xl"
        >
          <motion.div variants={itemVariants}>
            <h1 className="text-3xl font-extrabold tracking-tight text-[var(--foreground)] sm:text-4xl">
              Terms & <span className="text-[var(--accent)]">Conditions</span>
            </h1>
            <p className="mt-2 text-sm text-[var(--foreground-muted)]">Last updated: April 8, 2026</p>
          </motion.div>

          <motion.div variants={itemVariants} className="prose prose-invert max-w-none text-[var(--foreground)]/80 prose-headings:text-[var(--foreground)] prose-a:text-[var(--accent)]">
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using this website, you accept and agree to be bound by the terms and provisions of this agreement. In addition, when using these particular services, you shall be subject to any posted guidelines or rules applicable to such services.
            </p>

            <h2>2. Provision of Services</h2>
            <p>
              We are constantly innovating in order to provide the best possible experience for our users. You acknowledge and agree that the form and nature of the services which we provide may change from time to time without prior notice to you.
            </p>

            <h2>3. Intellectual Property</h2>
            <p>
              Unless otherwise stated, we or our licensors own the intellectual property rights for all material on the website. All intellectual property rights are reserved. You may view and/or print pages from the website for your own personal use subject to restrictions set in these terms and conditions.
            </p>

            <h2>4. Limitation of Liability</h2>
            <p>
              In no event shall we be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your access to or use of or inability to access or use the services.
            </p>
            
            <h2>5. Governing Law</h2>
            <p>
              These Terms shall be governed and construed in accordance with the laws of the applicable jurisdiction, without regard to its conflict of law provisions.
            </p>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
}
